/**
 * Billing > Rate cards (Phase 21 / Task 21.7b): the price lists customers subscribe to.
 * GET/POST /v1/platform/billing/rate-cards. A card is for one kind of customer (hauler,
 * operator or disposal company); its prices live in dated versions, each published once.
 */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ariaSort, listFrom, sortRows, useUrlFlag, useUrlSort } from '../../lib/url-state';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { SortButton } from 'src/components/ui/sortable-head';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import { useCreateRateCard, useRateCardList, type CustomerTenantType } from './billing-hooks';
import { billingErrorText, templateLabel } from './billing-format';
import { FEATURE_TEMPLATES, type FeatureTemplate } from '@lodex-pro/shared/features';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** The hauler feature plans a card can sell; "" = none named. */
export function TemplateSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: FeatureTemplate | '';
  onChange: (v: FeatureTemplate | '') => void;
}): React.JSX.Element {
  return (
    <select
      id={id}
      value={value}
      onChange={(e): void => onChange(e.target.value as FeatureTemplate | '')}
      style={fieldStyle}
    >
      <option value="">Not named</option>
      {FEATURE_TEMPLATES.map((t) => (
        <option key={t} value={t}>
          {templateLabel(t)}
        </option>
      ))}
    </select>
  );
}

const TYPE_LABELS: Readonly<Record<CustomerTenantType, string>> = {
  HAULER: 'Haulers',
  OPERATOR: 'Operators',
  DISPOSAL: 'Disposal companies',
};

/**
 * The columns the rate-card list sorts by (OPEN-204). The API sends them by customer type in the
 * tenant_type order (hauler, operator, disposal), then name; the For sort keeps that order.
 */
const RATE_CARD_SORTS = ['name', 'type', 'inForce', 'versions'] as const;
const TYPE_RANK: Readonly<Record<CustomerTenantType, number>> = {
  HAULER: 0,
  OPERATOR: 1,
  DISPOSAL: 2,
};

function NewCardForm({ onDone }: { onDone: () => void }): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [tenantType, setTenantType] = React.useState<CustomerTenantType>('HAULER');
  const [description, setDescription] = React.useState('');
  const [template, setTemplate] = React.useState<FeatureTemplate | ''>('');
  const create = useCreateRateCard({
    onSuccess: () => {
      toast.success('Rate card created. Add its first version.');
      onDone();
    },
    onError: (err) => toast.error(billingErrorText(err, 'Could not create the rate card.')),
  });
  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (name.trim() === '') return;
        create.mutate({
          name: name.trim(),
          tenantType,
          description: description.trim() === '' ? null : description.trim(),
          // 0232: only a hauler card names the feature plan it sells.
          ...(tenantType === 'HAULER' && template !== '' ? { featureTemplate: template } : {}),
        });
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '520px',
        marginBottom: '20px',
      }}
    >
      <FormRow label="Name" htmlFor="rc-name" required>
        <input
          id="rc-name"
          value={name}
          onChange={(e): void => setName(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="For" htmlFor="rc-type">
        <select
          id="rc-type"
          value={tenantType}
          onChange={(e): void => setTenantType(e.target.value as CustomerTenantType)}
          style={fieldStyle}
        >
          {(['HAULER', 'OPERATOR', 'DISPOSAL'] as const).map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </FormRow>
      {tenantType === 'HAULER' && (
        <FormRow label="Sells hauler plan" htmlFor="rc-template">
          <TemplateSelect id="rc-template" value={template} onChange={setTemplate} />
        </FormRow>
      )}
      <FormRow label="Description" htmlFor="rc-desc">
        <input
          id="rc-desc"
          value={description}
          onChange={(e): void => setDescription(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button type="submit" disabled={name.trim() === '' || create.isPending}>
          Create rate card
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function RateCardsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // OPEN-191 — in the URL, so a rate card's Back returns to the same list.
  const [showArchived, setShowArchived] = useUrlFlag('archived', []);
  const [creating, setCreating] = React.useState(false);
  const { data, isLoading, isError } = useRateCardList(showArchived);
  const { sort, toggle } = useUrlSort(RATE_CARD_SORTS, { key: 'type', dir: 'asc' }, []);
  const cards = sortRows(data?.rateCards ?? [], sort, (c, k) => {
    switch (k) {
      case 'name':
        return c.name;
      case 'type':
        return TYPE_RANK[c.tenantType];
      case 'inForce':
        return c.latestPublishedVersion?.effectiveFrom;
      case 'versions':
        return c.versionCount;
    }
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
        {!creating && (
          <Button type="button" onClick={(): void => setCreating(true)}>
            New rate card
          </Button>
        )}
        <label style={{ ...subText, display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e): void => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>
      {creating && <NewCardForm onDone={(): void => setCreating(false)} />}
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the rate cards.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'name')}>
                <SortButton column="name" sort={sort} onSort={toggle}>
                  Rate card
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'type')}>
                <SortButton column="type" sort={sort} onSort={toggle}>
                  For
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'inForce')}>
                <SortButton column="inForce" sort={sort} onSort={toggle}>
                  In force from
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'versions')}>
                <SortButton column="versions" sort={sort} onSort={toggle}>
                  Versions
                </SortButton>
              </th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c) => (
              <tr
                key={c.id}
                data-testid={`rate-card-${c.id}`}
                onClick={(): void => {
                  void navigate(`/billing/rate-cards/${c.id}`, { state: listFrom(location) });
                }}
                style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={{ padding: '8px' }}>
                  {c.name} {c.isArchived && <Pill tone="muted" label="Archived" />}
                </td>
                <td style={{ padding: '8px' }}>{TYPE_LABELS[c.tenantType]}</td>
                <td style={{ padding: '8px' }}>
                  {c.latestPublishedVersion === null ? (
                    <Pill tone="warning" label="Not published" />
                  ) : (
                    `v${String(c.latestPublishedVersion.versionNumber)}, ${c.latestPublishedVersion.effectiveFrom}`
                  )}
                </td>
                <td style={{ padding: '8px', ...subText }}>{formatNumber(c.versionCount)}</td>
              </tr>
            ))}
            {data.rateCards.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: '12px 8px', ...subText }}>
                  No rate cards yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
